import axios, { AxiosProgressEvent } from 'axios'
import { useCallback, useState } from 'react'

import { useCompleteAssetUpload, useInitAssetUpload } from '@/api/api-mutations'
import type { Asset, AssetCategoryType } from '@/api/common-types'

export type UploadStatus = 'idle' | 'creating' | 'uploading' | 'finalizing' | 'completed' | 'failed'

export type AssetUploadResult = {
	asset: Asset
}

export type UseAssetUploadOptions = {
	category: AssetCategoryType
	tags?: string[]
}

export type UploadOptions = {
	tags?: string[]
}

export const useAssetUpload = ({ category, tags }: UseAssetUploadOptions) => {
	const [status, setStatus] = useState<UploadStatus>('idle')
	const [progress, setProgress] = useState(0)
	const [error, setError] = useState<Error | null>(null)

	const initAssetUpload = useInitAssetUpload()
	const completeAssetUpload = useCompleteAssetUpload()

	const upload = useCallback(
		async (file: File, options?: UploadOptions): Promise<AssetUploadResult> => {
			setError(null)
			setProgress(0)
			setStatus('creating')
			try {
				const initResult = await initAssetUpload({
					body: {
						filename: file.name,
						contentType: file.type || 'application/octet-stream',
						size: file.size,
						category,
						tags: options?.tags ?? tags
					}
				})
				if (!initResult.isSuccess) {
					throw new Error(`Failed to initialize upload (status ${initResult.status})`)
				}
				const init = initResult.response

				setStatus('uploading')
				await axios.put(init.uploadUrl, file, {
					headers: { 'Content-Type': file.type || 'application/octet-stream' },
					onUploadProgress: (event: AxiosProgressEvent) => {
						if (event.total) {
							setProgress(Math.round((event.loaded / event.total) * 100))
						}
					}
				})

				setStatus('finalizing')
				const completedResult = await completeAssetUpload({
					path: { assetUuid: init.asset.uuid }
				})
				if (!completedResult.isSuccess) {
					throw new Error(`Failed to finalize upload (status ${completedResult.status})`)
				}

				setStatus('completed')
				return { asset: completedResult.response }
			} catch (e: any) {
				setStatus('failed')
				const err = e instanceof Error ? e : new Error(String(e))
				setError(err)
				throw err
			}
		},
		[completeAssetUpload, initAssetUpload, category, tags]
	)

	const reset = useCallback(() => {
		setStatus('idle')
		setProgress(0)
		setError(null)
	}, [])

	return {
		upload,
		reset,
		status,
		progress,
		error,
		isUploading: status === 'creating' || status === 'uploading' || status === 'finalizing'
	}
}
